import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Axios from '../../../../common/util/Axios';
import store from '../../../../store';

const EMPTY_AMOUNTS = {};

const classifyAmount = (raw) => {
  const text = String(raw ?? '').trim();
  if (!text) return { state: 'blank' };
  const parsed = Number(text);
  if (!Number.isFinite(parsed)) return { state: 'invalid' };
  const amount = Math.round(parsed * 100) / 100;
  if (amount < 0) return { state: 'invalid' };
  if (amount === 0) return { state: 'blank' };
  return { state: 'amount', amount };
};

const eligibleReturns = (rows, excludeId) =>
  (Array.isArray(rows) ? rows : []).filter((row) => {
    const status = row?.status;
    const open = status === 'Open' || status === 'Partially Settled';
    if (!open || classifyAmount(row?.availableCredit).state !== 'amount') {
      return false;
    }
    if (excludeId && String(row.id) === String(excludeId)) {
      return false;
    }
    return true;
  });

export const ApplyCredit = ({
  supplierId,
  excludeId,
  amounts = EMPTY_AMOUNTS,
  onAmountsChange,
  onChange,
}) => {
  const [options, setOptions] = useState([]);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const amountMap = amounts && typeof amounts === 'object' ? amounts : EMPTY_AMOUNTS;

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const companyId = store.getState().authState.companyId;
      const branchId = store.getState().authState.branchId;
      if (!supplierId || !branchId) {
        setOptions([]);
        return;
      }
      try {
        const res = await Axios.get('/api/dashboard/supplier-return/find-all', {
          params: { companyId, branchId, supplierId },
        });
        if (!cancelled) {
          setOptions(Array.isArray(res.data?.data) ? res.data.data : []);
        }
      } catch {
        if (!cancelled) setOptions([]);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [supplierId]);

  const visible = eligibleReturns(options, excludeId);

  useEffect(() => {
    let invalid = false;
    const applied = [];
    for (const row of eligibleReturns(options, excludeId)) {
      const id = String(row.id);
      const classified = classifyAmount(amountMap[id]);
      if (classified.state === 'invalid') {
        invalid = true;
        continue;
      }
      if (classified.state === 'amount') {
        applied.push({ supplierReturnId: id, amount: classified.amount });
      }
    }
    onChangeRef.current(applied, invalid);
  }, [options, excludeId, amountMap]);

  if (!supplierId) return null;

  return (
    <section aria-label="Apply">
      {visible.map((row) => (
        <div key={row.id}>
          <span>{row.returnNo}</span>
          <span>{row.status}</span>
          <span>{`Available Credit ${row.availableCredit}`}</span>
          <input
            aria-label={`Apply ${row.returnNo}`}
            value={amountMap[String(row.id)] || ''}
            onChange={(event) => {
              const next = event.target.value;
              onAmountsChange({ ...amountMap, [String(row.id)]: next });
            }}
          />
        </div>
      ))}
    </section>
  );
};

ApplyCredit.propTypes = {
  supplierId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  excludeId: PropTypes.string,
  amounts: PropTypes.object,
  onAmountsChange: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
};
